import type {
  AdminV2LiveAlert,
  AdminV2LiveAlertDetailResponse,
  AdminV2LiveAlertQuickAction,
  AdminV2LiveAlertQuickActionId,
} from "@/types/adminV2";
import { isFormsSubmissionReviewAlert } from "@/vnext/routes/support/live-alerts/liveAlertsFormsReviewModel";
import {
  getAudiencesConsumerHandoffOwnerAction,
  getAutomationEmailReviewOwnerAction,
} from "@/vnext/routes/support/live-alerts/liveAlertsRouteModel";

const LIVE_ALERT_BUSINESS_TIME_ZONE = "Pacific/Auckland";
const LIVE_ALERT_BUSINESS_START_HOUR = 9;
const LIVE_ALERT_BUSINESS_END_HOUR = 17;
const LIVE_ALERT_BUSINESS_MINUTES_PER_DAY =
  (LIVE_ALERT_BUSINESS_END_HOUR - LIVE_ALERT_BUSINESS_START_HOUR) * 60;
const LIVE_ALERT_BUSINESS_STEP_MINUTES = 15;

export type LiveAlertDrawerView = "action" | "activity" | "source";
export type LiveAlertDrawerShortcutAction =
  | "add_note"
  | "owner_workflow"
  | "review_source"
  | "suppress"
  | "resolve"
  | "close";

export interface LiveAlertDrawerShortcutRequest {
  action: LiveAlertDrawerShortcutAction;
  alertId: string;
  requestId: number;
}

export const LIVE_ALERT_DRAWER_VIEWS: ReadonlyArray<{
  id: LiveAlertDrawerView;
  label: string;
}> = [
  { id: "action", label: "Action" },
  { id: "activity", label: "Activity" },
  { id: "source", label: "Source" },
];

export const getLiveAlertQuickActions = (
  alert: AdminV2LiveAlert,
  detail?: AdminV2LiveAlertDetailResponse,
): AdminV2LiveAlertQuickAction[] =>
  detail?.quickActions ??
  detail?.alert.quickActions ??
  alert.quickActions ??
  [];

const isReviewOnlyLiveAlertAction = (
  action: AdminV2LiveAlertQuickAction,
): boolean => action.id === "open_source" || action.id === "view_action_feed";

const TAKE_ACTION_OWNER_HANDOFF_PRIORITY: readonly AdminV2LiveAlertQuickActionId[] =
  [
    "open_messaging",
    "email_user_draft",
    "reply_support",
    "open_crm_contact",
    "manage_crm_tags",
    "open_issue_report",
    "open_feature_request",
    "view_billing_information",
  ];

const BILLING_TAKE_ACTION_OWNER_HANDOFF_PRIORITY: readonly AdminV2LiveAlertQuickActionId[] =
  ["view_billing_information"];

const getPreferredOwnerHandoffAction = (
  actions: readonly AdminV2LiveAlertQuickAction[],
  priority: readonly AdminV2LiveAlertQuickActionId[] = TAKE_ACTION_OWNER_HANDOFF_PRIORITY,
): AdminV2LiveAlertQuickAction | null => {
  for (const actionId of priority) {
    const action = actions.find(
      (item) =>
        item.available &&
        item.id === actionId &&
        !isReviewOnlyLiveAlertAction(item),
    );
    if (action) {
      return action;
    }
  }

  return null;
};

export const getLiveAlertTakeAction = (
  alert: AdminV2LiveAlert,
  availableQuickActions: AdminV2LiveAlertQuickAction[],
  sourceContextMetadata: unknown,
): AdminV2LiveAlertQuickAction | null => {
  if (isFormsSubmissionReviewAlert(alert)) {
    const formSubmissionAction = availableQuickActions.find(
      (action) => action.available && action.id === "open_source",
    );
    if (formSubmissionAction) {
      return formSubmissionAction;
    }
  }

  const audiencesOwnerAction = getAudiencesConsumerHandoffOwnerAction(
    availableQuickActions,
    sourceContextMetadata,
  );
  if (audiencesOwnerAction) {
    return audiencesOwnerAction;
  }

  const ownerAction = getAutomationEmailReviewOwnerAction(
    availableQuickActions,
    sourceContextMetadata,
  );
  if (ownerAction) {
    return ownerAction;
  }

  if (alert.source === "billing_event") {
    const billingOwnerAction = getPreferredOwnerHandoffAction(
      availableQuickActions,
      BILLING_TAKE_ACTION_OWNER_HANDOFF_PRIORITY,
    );
    if (billingOwnerAction) {
      return billingOwnerAction;
    }
  }

  const preferredOwnerAction = getPreferredOwnerHandoffAction(
    availableQuickActions,
  );
  if (preferredOwnerAction) {
    return preferredOwnerAction;
  }

  return (
    availableQuickActions.find(
      (action) => !isReviewOnlyLiveAlertAction(action),
    ) ?? null
  );
};

export const toDatetimeLocalInputValue = (isoValue: string | null): string => {
  if (!isoValue) {
    return "";
  }
  const date = new Date(isoValue);
  if (!Number.isFinite(date.getTime())) {
    return "";
  }
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hour = String(date.getHours()).padStart(2, "0");
  const minute = String(date.getMinutes()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}T${hour}:${minute}`;
};

const liveAlertBusinessDateTimeFormat = new Intl.DateTimeFormat("en-NZ", {
  timeZone: LIVE_ALERT_BUSINESS_TIME_ZONE,
  weekday: "short",
  hour: "2-digit",
  hourCycle: "h23",
});

export const isLiveAlertBusinessTime = (date: Date): boolean => {
  const parts = liveAlertBusinessDateTimeFormat.formatToParts(date);
  const weekday = parts.find((part) => part.type === "weekday")?.value ?? "";
  const hour = Number(parts.find((part) => part.type === "hour")?.value ?? 0);
  return (
    weekday !== "Sat" &&
    weekday !== "Sun" &&
    hour >= LIVE_ALERT_BUSINESS_START_HOUR &&
    hour < LIVE_ALERT_BUSINESS_END_HOUR
  );
};

export const addLiveAlertBusinessMinutes = (
  value: Date,
  minutes: number,
): Date => {
  const cursor = Number.isFinite(value.getTime())
    ? new Date(value)
    : new Date();
  let remaining = Math.max(0, minutes);

  while (remaining > 0) {
    const next = new Date(
      cursor.getTime() + LIVE_ALERT_BUSINESS_STEP_MINUTES * 60_000,
    );
    if (isLiveAlertBusinessTime(cursor)) {
      remaining -= LIVE_ALERT_BUSINESS_STEP_MINUTES;
    }
    cursor.setTime(next.getTime());
  }

  return cursor;
};

export const getDefaultSuppressedUntilInput = (
  baseDate: Date = new Date(),
): string =>
  toDatetimeLocalInputValue(
    addLiveAlertBusinessMinutes(
      baseDate,
      2 * LIVE_ALERT_BUSINESS_MINUTES_PER_DAY,
    ).toISOString(),
  );

export const getIsoFromDatetimeLocalInput = (value: string): string | null => {
  if (!value.trim()) {
    return null;
  }
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toISOString() : null;
};

export const getLiveAlertShortcutDrawerView = (
  action: LiveAlertDrawerShortcutAction,
): LiveAlertDrawerView => {
  switch (action) {
    case "add_note":
      return "activity";
    case "resolve":
    case "close":
    case "owner_workflow":
    case "suppress":
      return "action";
    case "review_source":
      return "source";
  }
};

/** A local source pointer can only reopen this alert's verified context. */
export const isLiveAlertContextDestination = (
  path: string | null | undefined,
  alertId: string,
): boolean => {
  if (!path?.startsWith("/") || path.startsWith("//")) return false;
  const destination = new URL(path, "https://admin.invalid");
  return (
    destination.pathname === "/live-alerts" &&
    destination.searchParams.get("alert") === alertId
  );
};
